import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getUserAssignments, getAssignment, deleteAssignment } from '../../services/databaseService';
import NavBar from './NavBar';
import Footer from '../../Footer';
import './AssignmentHistory.css';

function AssignmentHistory() {
  const { currentUser } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  useEffect(() => {
    if (currentUser) {
      loadAssignments();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [currentUser]);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      setError(null);
      const userAssignments = await getUserAssignments(currentUser.uid);
      setAssignments(userAssignments);
    } catch (err) {
      console.error('Error loading assignments:', err);
      setError('Failed to load assignments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAssignment = async (assignmentId) => {
    try {
      const assignment = await getAssignment(assignmentId);
      setSelectedAssignment(assignment);
    } catch (err) {
      console.error('Error loading assignment:', err);
      alert('Failed to load assignment details.');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }
    try {
      await deleteAssignment(assignmentId);
      setAssignments(assignments.filter(a => a.id !== assignmentId));
      if (selectedAssignment && selectedAssignment.id === assignmentId) {
        setSelectedAssignment(null);
      }
      alert('Task deleted successfully.');
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Failed to delete task.');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!currentUser) {
    return (
      <div>
        <NavBar />
        <div className="assignment-history-container">
          <div className="auth-required">
            <h2>Please Sign In</h2>
            <p>You need to be signed in to view your task history.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <NavBar />
        <div className="assignment-history-container">
          <div className="loading">Loading your tasks...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (selectedAssignment) {
    return (
      <div>
        <NavBar />
        <div className="assignment-history-container">
          <button className="back-btn" onClick={() => setSelectedAssignment(null)}>
            ← Back to History
          </button>
          <div className="assignment-detail">
            <h2>{selectedAssignment.assignmentTopic}</h2>
            <p className="assignment-date">
              Created: {formatDate(selectedAssignment.createdAt)}
            </p>
            {selectedAssignment.summary && (
              <div className="detail-section">
                <h3>Summary</h3>
                <p>{selectedAssignment.summary}</p>
              </div>
            )}
            {selectedAssignment.selectedOpinion && (
              <div className="detail-section">
                <h3>Selected Opinion</h3>
                <p>{selectedAssignment.selectedOpinion}</p>
              </div>
            )}
            <div className="detail-section">
              <h3>Generated Task</h3>
              <div 
                className="assignment-text"
                dangerouslySetInnerHTML={{ 
                  __html: selectedAssignment.finalAssignment
                    ? selectedAssignment.finalAssignment
                        .replace(/^[-•]\s+/gm, '') // Remove leading dashes and bullet points
                        .replace(/\n[-•]\s+/g, '\n') // Remove dashes and bullet points at line start
                        .split(/\n\n+/) // Split by double newlines (paragraphs)
                        .map(para => {
                          // Remove any remaining leading/trailing dashes
                          para = para.trim().replace(/^[-•]\s+/, '').replace(/\s+[-•]$/, '');
                          // If paragraph contains HTML, preserve it
                          if (/<span style=/.test(para)) {
                            return `<p>${para.replace(/\n/g, '<br />')}</p>`;
                          }
                          // Plain text paragraph
                          return `<p>${para.replace(/\n/g, '<br />')}</p>`;
                        })
                        .join('')
                    : ''
                }}
              />
            </div>
            {selectedAssignment.verifiedSources && selectedAssignment.verifiedSources.length > 0 && (
              <div className="detail-section">
                <h3>Verified Sources</h3>
                <ul>
                  {selectedAssignment.verifiedSources.map((source, index) => (
                    <li key={index}>
                      <strong>{source.title}</strong>
                      {source.author && <> - {source.author}</>}
                      {source.description && <p>{source.description}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="detail-actions">
              <button
                className="delete-btn"
                onClick={() => handleDeleteAssignment(selectedAssignment.id)}
              >
                Delete Task
              </button>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    );
  }

  return (
    <div>
      <NavBar />
      <div className="assignment-history-container">
        <div className="history-header">
          <h2>My Tasks</h2>
          <p>View and manage your generated tasks</p>
        </div>
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        {assignments.length === 0 ? (
          <div className="empty-state">
            <p>You haven't generated any tasks yet.</p>
            <p>Start by creating a new task on the home page!</p>
          </div>
        ) : (
          <div className="assignments-list">
            {assignments.map((assignment) => (
              <div key={assignment.id} className="assignment-card">
                <div className="assignment-card-header">
                  <h3>{assignment.assignmentTopic}</h3>
                  <span className="assignment-date">
                    {formatDate(assignment.createdAt)}
                  </span>
                </div>
                {assignment.summary && (
                  <p className="assignment-summary">
                    {assignment.summary.substring(0, 150)}...
                  </p>
                )}
                <div className="assignment-card-actions">
                  <button
                    className="view-btn"
                    onClick={() => handleViewAssignment(assignment.id)}
                  >
                    View Task
                  </button>
                  <button
                    className="delete-btn-small"
                    onClick={() => handleDeleteAssignment(assignment.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default AssignmentHistory;
